import { useEffect, useRef, useState } from 'react';

import { Settings01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { PartnersCodes } from 'constants/partners';
import { PartnersConfig } from 'constants/partners-config';
import patternValidators, { normalizeEmailList } from 'helpers/validators/pattern-validators';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { SearchInput } from 'components/FormComponents';
import * as Integration from 'components/Integrations/index';

import { revokePartnerToken } from 'api/partner-token-version-slice';
import {
  connectCornerstone,
  connectGreenhouse,
  connectJobAdder,
  connectJobylon,
  connectLever,
  connectLivehire,
  disconnectCornerstone,
  disconnectGreenhouse,
  disconnectJobAdder,
  disconnectJobylon,
  disconnectLever,
  disconnectLivehire,
  getPartnersConfig,
  regenerateCornerstonePassword,
  selectPartnersConfig,
  updatePartnersConfig,
} from 'api/partners-config-slice';
import { useGetCompanyUsersQuery } from 'api/user-api';

import { IntegrationsSettingsModal } from './IntegrationsSettingsModal';

export const Integrations = ({ user, company, setLoading = () => {}, popUpMessage = () => {} }) => {
  const dispatch = useDispatch();

  const partners = useSelector(selectPartnersConfig);

  const { data: companyUsers } = useGetCompanyUsersQuery(user?.id, {
    refetchOnMountOrArgChange: true,
  });

  const onConnectJobAdder = code => dispatch(connectJobAdder(code));
  const onDisconnectJobAdder = () => dispatch(disconnectJobAdder());
  const onConnectLever = code => dispatch(connectLever(code));
  const onDisconnectLever = () => dispatch(disconnectLever());
  const onConnectLivehire = params => dispatch(connectLivehire(params));
  const onDisconnectLivehire = () => dispatch(disconnectLivehire());
  const onConnectCornerstone = () => dispatch(connectCornerstone());
  const onDisconnectCornerstone = () => dispatch(disconnectCornerstone());
  const onRegenerateCornerstonePassword = () => dispatch(regenerateCornerstonePassword());
  const onConnectGreenhouse = params => dispatch(connectGreenhouse(params));
  const onDisconnectGreenhouse = () => dispatch(disconnectGreenhouse());
  const onConnectJobylon = params => dispatch(connectJobylon(params));
  const onDisconnectJobylon = () => dispatch(disconnectJobylon());
  const onGetPartnersConfig = () => dispatch(getPartnersConfig());
  const onPartnersConfigUpdate = params => dispatch(updatePartnersConfig(params));
  const onRevokePartnerToken = partner => dispatch(revokePartnerToken(partner));

  const [useIntegrationUser, setUseIntegrationUser] = useState(!!partners.config.user_id);
  const [notificationEmails, setNotificationEmails] = useState(
    partners.config.notification_emails || '',
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const discardNotificationEdit = useRef(false);

  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'team@checkmate.tech';

  useEffect(() => {
    if (!partners.fetched) return;
    setNotificationEmails(partners.config.notification_emails || '');
  }, [partners.config.notification_emails]);

  useEffect(() => {
    if (!partners.fetched) return;

    const shouldUseIntegrationUser = !!partners.config.user_id;
    setUseIntegrationUser(shouldUseIntegrationUser);

    // If shouldUseIntegrationUser is true and we already have a user_id, don't change anything
    if (shouldUseIntegrationUser && partners?.config?.user_id) return;

    const value = shouldUseIntegrationUser ? company.creator_id : null;
    setLoading(true);
    onPartnersConfigUpdate({ user_id: value }).then(() => setLoading(false));
  }, [partners.fetched]);

  useEffect(() => {
    setLoading(true);
    onGetPartnersConfig().then(_ => setLoading(false));
  }, []);

  const handleTokenRevoke = async partner => {
    setLoading(true);
    await onRevokePartnerToken(partner);
    setLoading(false);
  };

  const getPartnerIntegration = config => {
    if (!partners.fetched) return;

    const code = config.partnerCode ?? config.name;

    switch (code) {
      case PartnersCodes.AccountApi:
        return (
          <Integration.AccountApiIntegration
            key={config.name}
            config={config}
            integration={partners.account_api}
            revokeToken={handleTokenRevoke}
          />
        );
      case PartnersCodes.TAS:
        return (
          <Integration.TasIntegration
            key={config.name}
            config={config}
            integration={partners.tas}
            setLoading={setLoading}
            updatePartnersConfig={onPartnersConfigUpdate}
          />
        );
      case PartnersCodes.JobAdder:
        return (
          <Integration.JobAdderIntegration
            key={config.name}
            config={config}
            integration={partners.job_adder}
            connect={onConnectJobAdder}
            disconnect={onDisconnectJobAdder}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
          />
        );
      case PartnersCodes.JazzHR:
        return (
          <Integration.JazzIntegration
            key={config.name}
            config={config}
            integration={partners.jazzhr}
            revokeToken={handleTokenRevoke}
          />
        );
      case PartnersCodes.Teamtailor:
        return (
          <Integration.TeamtailorIntegration
            key={config.name}
            config={config}
            integration={partners.teamtailor}
            revokeToken={handleTokenRevoke}
          />
        );
      case PartnersCodes.Lever:
        return (
          <Integration.LeverIntegration
            key={config.name}
            config={config}
            integration={partners.lever}
            connect={onConnectLever}
            disconnect={onDisconnectLever}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
            updatePartnersConfig={onPartnersConfigUpdate}
          />
        );
      case PartnersCodes.PageUp:
        return (
          <Integration.PageUpIntegration
            key={config.name}
            config={config}
            integration={partners.pageup}
            updatePartnersConfig={onPartnersConfigUpdate}
            setLoading={setLoading}
          />
        );
      case PartnersCodes.Livehire:
        return (
          <Integration.LivehireIntegration
            key={config.name}
            config={config}
            integration={partners.livehire}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
            connectLivehire={onConnectLivehire}
            disconnectLivehire={onDisconnectLivehire}
          />
        );
      case PartnersCodes.Cornerstone:
        return (
          <Integration.CornerstoneIntegration
            key={config.name}
            config={config}
            integration={partners.cornerstone}
            connectCornerstone={onConnectCornerstone}
            disconnectCornerstone={onDisconnectCornerstone}
            regenerateCornerstonePassword={onRegenerateCornerstonePassword}
            updatePartnersConfig={onPartnersConfigUpdate}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
          />
        );
      case PartnersCodes.Greenhouse:
        return (
          <Integration.GreenhouseIntegration
            key={config.name}
            config={config}
            integration={partners.greenhouse}
            connect={onConnectGreenhouse}
            disconnect={onDisconnectGreenhouse}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
          />
        );
      case PartnersCodes.Jobylon:
        return (
          <Integration.JobylonIntegration
            key={config.name}
            config={config}
            integration={partners.jobylon}
            setLoading={setLoading}
            popUpMessage={popUpMessage}
            connectJobylon={onConnectJobylon}
            disconnectJobylon={onDisconnectJobylon}
          />
        );
      default:
        return (
          <Integration.IntegrationCard
            key={config.name}
            logo={config.logo}
            title={config.title}
            documentationUrl={config?.documentationUrl}
          />
        );
    }
  };

  const searchLower = searchQuery.toLocaleLowerCase();
  const filteredPartners = PartnersConfig.filter(partner =>
    partner.title.toLocaleLowerCase().includes(searchLower),
  );
  const connectablePartners = filteredPartners.filter(p => !p.infoOnly);
  const contactUsPartners = filteredPartners.filter(p => p.infoOnly);

  const updateUser = async e => {
    setLoading(true);
    await onPartnersConfigUpdate({ user_id: e.target.value });
    setLoading(false);
  };

  const handleUseIntegrationUserChange = async e => {
    setUseIntegrationUser(e.target.checked);
    if (!e.target.checked) {
      await onPartnersConfigUpdate({ user_id: null });
    } else {
      await updateUser({ target: { value: partners?.config?.user_id || company.creator_id } });
    }
  };

  const hanldePartnersConfigUpdate = async e => {
    const name = e.target.name;
    const value = e.target.checked ?? e.target.value;

    setLoading(true);
    await onPartnersConfigUpdate({ [name]: value });
    setLoading(false);
  };

  const notificationEmailError = patternValidators.emailList.validate(notificationEmails);

  const handleNotificationEmailsBlur = async () => {
    if (discardNotificationEdit.current) {
      discardNotificationEdit.current = false;
      return;
    }
    const normalized = normalizeEmailList(notificationEmails);
    setNotificationEmails(normalized);
    if (patternValidators.emailList.validate(normalized) !== true) return;
    if (normalized === (partners.config.notification_emails || '')) return;
    setLoading(true);
    await onPartnersConfigUpdate({ notification_emails: normalized });
    setLoading(false);
  };

  const dismissSettings = () => {
    const normalized = normalizeEmailList(notificationEmails);
    if (patternValidators.emailList.validate(normalized) !== true) {
      discardNotificationEdit.current = true;
      setNotificationEmails(partners.config.notification_emails || '');
    }
    setSettingsModalOpen(false);
  };

  const updateSettingsAndClose = () => {
    const normalized = normalizeEmailList(notificationEmails);
    if (patternValidators.emailList.validate(normalized) !== true) {
      setNotificationEmails(normalized);
      return;
    }
    setSettingsModalOpen(false);
  };

  return (
    <div>
      <IntegrationsSettingsModal
        visible={settingsModalOpen}
        onClose={dismissSettings}
        onUpdateAndClose={updateSettingsAndClose}
        useAuthOnResultFiles={partners.config.use_auth_on_result_files}
        onPartnersConfigUpdate={hanldePartnersConfigUpdate}
        useIntegrationUser={useIntegrationUser}
        onUseIntegrationUserChange={handleUseIntegrationUserChange}
        userId={partners.config.user_id}
        onUserIdChange={updateUser}
        companyUsers={companyUsers}
        notificationEmails={notificationEmails}
        notificationEmailError={notificationEmailError === true ? '' : notificationEmailError}
        onNotificationEmailsChange={e => {
          discardNotificationEdit.current = false;
          setNotificationEmails(e.target.value);
        }}
        onNotificationEmailsBlur={handleNotificationEmailsBlur}
      />
      <div>
        <div className="bg-neutral-1 rounded-3xl border border-neutral-4 p-8 flex flex-col gap-8">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-2">
              <h1 className="title-2">Integrations</h1>
              <p className="m-0 text-xl leading-snug text-text-secondary">
                Configure and activate integrations between Checkmate and other software platforms.
                <br />
                To request an integration, please contact{' '}
                <a className="u-link dark" href={`mailto:${supportEmail}`}>
                  {supportEmail}
                </a>
                .
              </p>
            </div>
            <div className="flex items-center gap-4">
              <OverlayTrigger trigger="hover" placement="top" overlay={<Tooltip>Settings</Tooltip>}>
                <span
                  className={`inline-flex shrink-0 text-primary-4 ${!partners.fetched ? 'cursor-default opacity-50' : 'cursor-pointer'}`}
                  role="presentation"
                  onClick={() => {
                    if (!partners.fetched) return;
                    discardNotificationEdit.current = false;
                    setNotificationEmails(partners.config.notification_emails || '');
                    setSettingsModalOpen(true);
                  }}>
                  <HugeiconsIcon icon={Settings01Icon} size="24" strokeWidth="2" />
                </span>
              </OverlayTrigger>
            </div>
          </div>
          <SearchInput
            placeholder="Search for an integration"
            className="u-width-25"
            onChange={setSearchQuery}
          />
        </div>
      </div>
      <div className="mt-12 grid w-full max-w-full grid-cols-4 justify-between gap-[30px]">
        {connectablePartners.map(config => getPartnerIntegration(config))}
      </div>
      {contactUsPartners.length > 0 && (
        <div className="mt-16 flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <h2 className="text-3xl font-medium">Request an integration</h2>
            <p className="text-xl text-text-secondary">
              Need help integrating any of the below, or want to request a new integration? Contact
              us at{' '}
              <a className="u-link dark" href={`mailto:${supportEmail}`}>
                {supportEmail}
              </a>
              .
            </p>
          </div>
          <div className="grid w-full max-w-full grid-cols-4 justify-between gap-[30px]">
            {contactUsPartners.map(config => (
              <Integration.ContactUsIntegrationCard key={config.name} config={config} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
